import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Share2, X as XIcon, Copy, Mail, MessageCircle, Send, Facebook, Linkedin, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HIDE = ["/login", "/register", "/reset", "/verify"];
let refCache;

function XLogo({ className }) { return <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M18.9 2H22l-7.3 8.3L23 22h-6.8l-5.3-6.9L4.8 22H1.7l7.8-8.9L0 2h7l4.8 6.3L18.9 2zm-1.2 18h1.9L7.1 3.9H5.1L17.7 20z" /></svg>; }

export function ShareFab() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open || ref !== null) return;
    const own = user ? api.get("/affiliates/me").then((r) => r.data.affiliate?.code || null).catch(() => null) : Promise.resolve(null);
    own.then(async (c) => {
      if (c) return setRef(c);
      if (refCache === undefined) refCache = await api.get("/public/guides").then((r) => r.data.ref || "").catch(() => "");
      setRef(refCache || "");
    });
  }, [open, ref, user]);
  if (HIDE.some((p) => pathname.startsWith(p))) return null;
  const url = () => { const u = new URL(window.location.origin + pathname); if (ref) u.searchParams.set("ref", ref); return u.toString(); };
  const title = () => document.title || "Fırsat Avcısı";
  const text = () => `${title()} — ${url()}`;
  const onClick = async () => {
    if (user) api.post("/credits/event", { kind: "share" }).then((r) => { if (r.data.added) toast.success(`+${r.data.added} kredi — paylaşım`); }).catch(() => {});
    if (navigator.share && window.innerWidth < 768) {
      try { await navigator.share({ title: title(), text: title(), url: url() }); } catch { /* cancelled */ }
      return;
    }
    setOpen((o) => !o);
  };
  const copy = async () => { try { await navigator.clipboard.writeText(url()); setCopied(true); toast.success("Link kopyalandı"); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Kopyalanamadı"); } };
  const links = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${encodeURIComponent(text())}`, "text-emerald-300"],
    ["X", XLogo, `https://twitter.com/intent/tweet?text=${encodeURIComponent(title())}&url=${encodeURIComponent(url())}`, "text-slate-200"],
    ["Telegram", Send, `https://t.me/share/url?url=${encodeURIComponent(url())}&text=${encodeURIComponent(title())}`, "text-sky-300"],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url())}`, "text-blue-300"],
    ["LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url())}`, "text-blue-200"],
    ["E-posta", Mail, `mailto:?subject=${encodeURIComponent(title())}&body=${encodeURIComponent(text())}`, "text-amber-200"],
  ];
  return (
    <div className="fixed bottom-24 right-5 z-[60] flex flex-col items-end gap-2 print:hidden" data-testid="share-fab">
      {open && (
        <div className="w-60 rounded-2xl border border-white/10 bg-[#0D111A]/95 p-3 shadow-2xl backdrop-blur-md" data-testid="share-panel">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold"><span>Bu sayfayı paylaş</span><button onClick={() => setOpen(false)} className="rounded-md p-1 hover:bg-white/5" aria-label="Kapat" data-testid="share-close"><XIcon className="h-3.5 w-3.5" /></button></div>
          <div className="grid grid-cols-3 gap-1.5">
            {links.map(([name, Icon, href, color]) => (
              <a key={name} href={href} target="_blank" rel="noopener noreferrer" className={`flex flex-col items-center gap-1 rounded-lg border border-white/5 px-1 py-2 text-[10px] text-slate-300 hover:bg-white/5 ${color}`} data-testid={`share-${name.toLowerCase().replace("-", "")}`}><Icon className="h-4 w-4" />{name}</a>
            ))}
          </div>
          <button onClick={copy} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500/15 px-2 py-1.5 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/25" data-testid="share-copy">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Linki kopyala</button>
          {ref && <p className="mt-1.5 text-center text-[10px] text-slate-500">Link {user ? "ortaklık kodunla" : "referans koduyla"} paylaşılır</p>}
        </div>
      )}
      <button onClick={onClick} className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-400 text-[#0A0D14] shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105 active:scale-95" aria-label="Paylaş" title="Bu sayfayı paylaş" data-testid="share-button"><Share2 className="h-5 w-5" /></button>
    </div>
  );
}
